import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import config from 'constants/config'
import { formatDateDMYCasual } from 'lib/datetime/dateUtils'
import { middleDotSeparatorMessage } from 'LuxKit/intl/separators'
import HeadData from 'components/Common/HeadData'

interface Props {
  offer: App.CruiseOffer
}

function CruiseHeadData(props: Props) {
  const { offer } = props
  const intl = useIntl()

  const purchasable = useMemo(() => {
    return offer?.departureDates && offer?.departureDates.length > 0
  }, [offer])

  const meta = useMemo(() => {
    const heroImageId = offer.heroImageId ?? offer.images[0]?.id
    const shipName = offer.ship?.name
    const cruiseLineName = offer.cruiseLine?.name

    const durationDescriptor = offer.duration ? intl.formatMessage(
      {
        id: '1OUrspk372E16wNfjJPz',
        defaultMessage: '{duration}-night',
        description: 'Duration descriptor for cruise meta description in the cruises section',
      },
      { duration: offer.duration },
    ) : undefined
    let heroSegment: string | undefined
    if (durationDescriptor && shipName) {
      heroSegment = `${durationDescriptor} ${shipName}`
    } else if (durationDescriptor) {
      heroSegment = intl.formatMessage(
        {
          id: 'RfjJP5mT9QC6KgwQzBlY',
          defaultMessage: '{durationDescriptor} cruise',
          description: 'Cruise type label with duration for meta description',
        },
        { durationDescriptor },
      )
    } else if (shipName) {
      heroSegment = intl.formatMessage(
        {
          id: 'mhQRzPZOcu_SWMZcI4jR',
          defaultMessage: '{shipName} cruise',
          description: 'Cruise label with ship name for meta description in the cruises section',
        },
        { shipName },
      )
    }

    const departurePort = offer.departurePort
    const returnPort = offer.returnPort
    const isRoundTrip = departurePort && returnPort && departurePort === returnPort
    const roundTripRoute = intl.formatMessage(
      {
        id: '5Z1IyqBPVj3uhKept3Kb',
        defaultMessage: '{departurePort} round trip',
        description: 'Route description for round trip cruise in the cruise offer or booking section',
      },
      { departurePort },
    )
    const oneWayRoute = intl.formatMessage(
      {
        id: 'nlcmpZr-xH2oyop7-C77',
        defaultMessage: '{departurePort} to {returnPort}',
        description: 'Route description for one-way cruise from departure port to return port',
      },
      { departurePort, returnPort },
    )
    const routeSegment = departurePort && returnPort
      ? (isRoundTrip ? roundTripRoute : oneWayRoute)
      : (departurePort || returnPort || undefined)

    const departureDate = offer.mainDepartureDetails?.departureDate
    const formattedDepartureDate = departureDate ? formatDateDMYCasual(departureDate) : undefined
    const departureSegment = formattedDepartureDate ? intl.formatMessage(
      {
        id: 'bDISKCkaIATu4Cdgs6DC',
        defaultMessage: 'Departs {departureDate}',
        description: 'Departure date for cruise meta description in the cruise section',
      },
      { departureDate: formattedDepartureDate },
    ) : undefined

    const descriptionParts: Array<string> = []
    if (heroSegment) descriptionParts.push(heroSegment)
    if (cruiseLineName) descriptionParts.push(cruiseLineName)
    if (routeSegment) descriptionParts.push(routeSegment)
    if (departureSegment) descriptionParts.push(departureSegment)

    const sep = intl.formatMessage(middleDotSeparatorMessage)
    const description = descriptionParts.length > 0
      ? intl.formatMessage(
          {
            id: 'xJnXLMDtDPWRdNqgGYT-',
            defaultMessage: '{details} — Book now with {brand}.',
            description: 'Cruise offer meta description with details and brand',
          },
          { details: descriptionParts.join(` ${sep} `), brand: config.title },
        )
      : intl.formatMessage(
          {
            id: '_jbpPfP5bkQOHExDNoig',
            defaultMessage: 'Book now with {brand}.',
            description: 'Fallback cruise meta description in the cruise offer or booking section',
          },
          { brand: config.title },
        )

    const titleSegments: Array<string> = [offer.name, shipName, cruiseLineName].filter((segment): segment is string => !!segment)
    const uniqueTitleSegments = Array.from(new Set(titleSegments))
    const title = uniqueTitleSegments.length > 0 ? uniqueTitleSegments.join(' | ') : offer.name

    return {
      title,
      description,
      imageId: heroImageId,
      imageAlt: offer.name,
    }
  }, [offer, intl])

  return <HeadData
    vertical="cruises"
    title={meta.title}
    description={meta.description}
    imageId={meta.imageId}
    imageAlt={meta.imageAlt}
    noIndex={!purchasable}
  />
}

export default CruiseHeadData
